import { useContext, useCallback, useMemo } from 'react';
import { AuthContext } from '@app/providers/firebase/AuthProvider';
import {
  Permission,
  UserPermissions,
  getUserPermissions,
  checkPermission,
  checkAllPermissions,
  getUsersWithPermission,
  getUsersWithAnyPermission,
} from './index';

/**
 * Hook return type for usePermissions
 */
export interface UsePermissionsReturn {
  /** Check if user has a specific permission (or any of multiple permissions) */
  hasPermission: (required: Permission | Permission[]) => boolean;
  /** Check if user has ALL of the specified permissions */
  hasAllPermissions: (required: Permission[]) => boolean;
  /** The user's permission list */
  permissions: Permission[];
  /** The user's explicitly denied permissions */
  restrictions: Permission[] | undefined;
  /** The user's default landing page */
  defaultPage: string;
  /** The user's display name (if configured) */
  displayName: string | undefined;
  /** Whether the user is a superuser (has '*' permission) */
  isSuperUser: boolean;
  /** The user's email address */
  userEmail: string | null;
  /** Whether the user has any permissions configured */
  isAuthenticated: boolean;
  /**
   * Get all users who have a specific permission.
   * Queries the in-code seed map — useful for admin tooling.
   */
  getUsersWithPermission: (permission: Permission) => string[];
  /**
   * Get all users who have ANY of the specified permissions.
   * Queries the in-code seed map — useful for admin tooling.
   */
  getUsersWithAnyPermission: (permissions: Permission[]) => string[];
}

/**
 * React hook for accessing the current user's permissions.
 *
 * Permissions are resolved from Firestore by AuthProvider on login and stored
 * in AuthContext.  This hook simply reads that already-resolved value — there
 * is no async work here.  The fallback to the in-code seed map is handled
 * transparently inside AuthProvider so callers always get a valid (possibly
 * empty) permissions array immediately.
 *
 * @example
 * ```tsx
 * const { hasPermission, isSuperUser } = usePermissions();
 *
 * // Check single permission
 * if (hasPermission('orders.edit')) {
 *   // Show edit button
 * }
 *
 * // Check any of multiple permissions
 * if (hasPermission(['orders.view', 'schedule.view'])) {
 *   // Show order info
 * }
 *
 * // Check all permissions required
 * if (hasAllPermissions(['orders.edit', 'orders.delete'])) {
 *   // Show dangerous action
 * }
 * ```
 */
export const usePermissions = (): UsePermissionsReturn => {
  const { currentUser, userPermissions: userConfig } = useContext(AuthContext);
  const email = currentUser?.email ?? null;

  const permissions = useMemo<Permission[]>(
    () => userConfig?.permissions ?? [],
    [userConfig]
  );

  const restrictions = useMemo<Permission[] | undefined>(
    () => userConfig?.restrictions,
    [userConfig]
  );

  const isSuperUser = useMemo(
    () => permissions.includes('*'),
    [permissions]
  );

  const hasPermission = useCallback(
    (required: Permission | Permission[]): boolean => {
      return checkPermission(permissions, required, restrictions);
    },
    [permissions, restrictions]
  );

  const hasAllPermissions = useCallback(
    (required: Permission[]): boolean => {
      return checkAllPermissions(permissions, required, restrictions);
    },
    [permissions, restrictions]
  );

  const getUsersWithPermissionCallback = useCallback(
    (permission: Permission): string[] => {
      return getUsersWithPermission(permission);
    },
    []
  );

  const getUsersWithAnyPermissionCallback = useCallback(
    (perms: Permission[]): string[] => {
      return getUsersWithAnyPermission(perms);
    },
    []
  );

  return {
    hasPermission,
    hasAllPermissions,
    permissions,
    restrictions,
    defaultPage: userConfig?.defaultPage ?? '/login',
    displayName: userConfig?.displayName,
    isSuperUser,
    userEmail: email,
    isAuthenticated: userConfig !== null,
    getUsersWithPermission: getUsersWithPermissionCallback,
    getUsersWithAnyPermission: getUsersWithAnyPermissionCallback,
  };
};

/**
 * Non-hook version for use outside of React components.
 * Requires the user's email to be passed explicitly.
 *
 * NOTE: This always uses the in-code seed map as the data source.  If you
 * need the Firestore-backed value outside of a component, call
 * `fetchUserPermissions(email)` from @services/db/permissions instead.
 *
 * @example
 * ```ts
 * const perms = getPermissionsForUser('keithh@wildwoodmfg.com');
 * if (perms.hasPermission('admin.settings')) {
 *   // Allow action
 * }
 * ```
 */
export const getPermissionsForUser = (email: string | null | undefined) => {
  const userConfig = getUserPermissions(email);
  const permissions = userConfig?.permissions ?? [];
  const restrictions = userConfig?.restrictions;

  return {
    hasPermission: (required: Permission | Permission[]): boolean => {
      return checkPermission(permissions, required, restrictions);
    },
    hasAllPermissions: (required: Permission[]): boolean => {
      return checkAllPermissions(permissions, required, restrictions);
    },
    permissions,
    restrictions,
    defaultPage: userConfig?.defaultPage ?? '/login',
    displayName: userConfig?.displayName,
    isSuperUser: permissions.includes('*'),
    isAuthenticated: userConfig !== null,
  };
};

export default usePermissions;
